<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"

import { ServerAPI } from "@/app/api/ServerAPI"
import { PluginAPI } from "@/app/api/PluginAPI"
import { ModuleAPI } from "@/app/api/ModuleAPI"
import { ServersEntity } from "@/app/entity/ServersEntity"
import { ModuleEntity } from "@/app/entity/ModuleEntity"
import { useAppStore } from "@/store/app"
import { useUserStore } from "@/store/user"
import { useSocketStore } from "@/store/sock"
import EmailValidation from "@/components/EmailValidation.vue"

const router = useRouter()
const { t, locale } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()
const socketStore = useSocketStore()
const serverAPI = new ServerAPI()
const pluginAPI = new PluginAPI()
const moduleAPI = new ModuleAPI()

const servers = ref<ServersEntity[]>([])
const serversLoading = ref(true)
const modules = ref<ModuleEntity[]>([])
const modulesLoading = ref(false)
const modulePage = ref(1)
const modulesPerPage = 5
const modulesTotal = ref(0)

const greeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 6) return t('home.greeting.lateNight')
  if (hour < 12) return t('home.greeting.morning')
  if (hour < 14) return t('home.greeting.noon')
  if (hour < 18) return t('home.greeting.afternoon')
  return t('home.greeting.evening')
})

const memberSince = computed(() => {
  const join = userStore.getJoinDate
  if (!join) return ""
  const d = new Date(join)
  const loc = locale.value === 'zh' ? 'zh-CN' : 'en-US'
  return d.toLocaleDateString(loc, { year: "numeric", month: "long" })
})

const memberExpiry = computed(() => {
  const exp = userStore.getMembershipExpiry
  if (!exp) return ""
  const d = new Date(exp)
  const loc = locale.value === 'zh' ? 'zh-CN' : 'en-US'
  return d.toLocaleDateString(loc, { year: "numeric", month: "short", day: "numeric" })
})

const onlineCount = computed(() => {
  return servers.value.filter(s => isServerOnline(s)).length
})

const proServerCount = computed(() => {
  return servers.value.filter(s => s.active).length
})

const moduleTotalPages = computed(() => Math.ceil(modulesTotal.value / modulesPerPage))

const isServerOnline = (server: ServersEntity): boolean => {
  if (!server.uuid) return false
  const status = socketStore.getServerStatus(server.uuid)
  return !!(status && status.status && !status.offline)
}

const fetchServers = async () => {
  serversLoading.value = true
  try {
    const data = await serverAPI.getAllServers(userStore.getUserId)
    servers.value = data
    // fetch online statuses
    const withUuid = data.filter(s => s.uuid)
    for (const server of withUuid) {
      try {
        const cachedStatus = await pluginAPI.getServerStatus(server.uuid!)
        if (cachedStatus) {
          if (cachedStatus.status === null) {
            socketStore.serverStatuses[server.uuid!] = {
              serverId: server.uuid!,
              status: null,
              timestamp: cachedStatus.timestamp || Date.now(),
              offline: true
            }
          } else {
            socketStore.serverStatuses[server.uuid!] = cachedStatus
          }
        }
      } catch {
        socketStore.serverStatuses[server.uuid!] = {
          serverId: server.uuid!,
          status: null,
          timestamp: Date.now(),
          offline: true
        }
      }
    }
  } catch {
    // silently fail, servers section will show empty
  } finally {
    serversLoading.value = false
  }
}

const fetchModules = async (page = 1) => {
  modulesLoading.value = true
  try {
    const result = await moduleAPI.getPluginList(page, modulesPerPage)
    modules.value = result.items
    modulesTotal.value = result.total
    modulePage.value = page
  } catch {
    // silently fail
  } finally {
    modulesLoading.value = false
  }
}

const quickLinks = computed(() => [
  { icon: "mdi-book-open-variant", label: t('home.quickLink.userDocs'), desc: t('home.quickLink.userDocsDesc'), href: "https://doc.ultikits.com", color: "#6366f1" },
  { icon: "mdi-code-tags", label: t('home.quickLink.devDocs'), desc: t('home.quickLink.devDocsDesc'), href: "https://dev.ultikits.com", color: "#22c55e" },
  { icon: "mdi-github", label: t('home.quickLink.github'), desc: t('home.quickLink.githubDesc'), href: "https://github.com/UltiKits", color: "#a78bfa" },
  { icon: "mdi-heart-pulse", label: t('home.quickLink.status'), desc: t('home.quickLink.statusDesc'), href: "https://status.ultikits.com", color: "#f59e0b" },
])

onMounted(() => {
  appStore.setNav(true)
  appStore.setLoading(false)
  fetchServers()
  fetchModules()
})
</script>

<template>
  <v-container class="home-page" fluid>
    <!-- Email Validation -->
    <email-validation />

    <!-- Hero Header -->
    <div class="hero-section mb-6">
      <div class="d-flex align-center flex-wrap ga-4">
        <div class="flex-grow-1">
          <div class="hero-greeting">{{ greeting }}{{ locale === 'zh' ? '，' : ', ' }}{{ userStore.getUsername || t('common.user') }}</div>
          <div class="hero-subtitle">
            <span v-if="userStore.isProMember" class="d-inline-flex align-center ga-1">
              <v-icon size="14" color="amber">mdi-crown</v-icon>
              <span class="text-amber font-weight-medium">{{ t('home.proMember') }}</span>
              <span class="text-disabled mx-1">&middot;</span>
            </span>
            <span v-if="memberSince" class="text-disabled">{{ t('home.joinedDate', { date: memberSince }) }}</span>
          </div>
        </div>
        <v-btn
          variant="tonal"
          color="primary"
          rounded="lg"
          @click="router.push('/server')"
        >
          <v-icon start>mdi-server</v-icon>
          {{ t('home.manageServers') }}
        </v-btn>
      </div>
    </div>

    <!-- Stats Row -->
    <v-row class="mb-6" dense>
      <v-col cols="6" sm="3" class="d-flex">
        <div class="stat-card flex-grow-1" @click="router.push('/server')">
          <div class="stat-icon" style="background: rgba(99, 102, 241, 0.1);">
            <v-icon size="20" color="#6366f1">mdi-server</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ servers.length }}</div>
            <div class="stat-label">{{ t('home.stats.totalServers') }}</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3" class="d-flex">
        <div class="stat-card flex-grow-1">
          <div class="stat-icon" style="background: rgba(34, 197, 94, 0.1);">
            <v-icon size="20" color="#22c55e">mdi-access-point</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ onlineCount }}</div>
            <div class="stat-label">{{ t('home.stats.onlineServers') }}</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3" class="d-flex">
        <div class="stat-card flex-grow-1">
          <div class="stat-icon" style="background: rgba(245, 158, 11, 0.1);">
            <v-icon size="20" color="#f59e0b">mdi-crown</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ proServerCount }}</div>
            <div class="stat-label">{{ t('home.stats.proServers') }}</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="3" class="d-flex">
        <div class="stat-card flex-grow-1">
          <div class="stat-icon" :style="{ background: socketStore.isConnected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)' }">
            <v-icon size="20" :color="socketStore.isConnected ? '#22c55e' : '#ef4444'">
              {{ socketStore.isConnected ? 'mdi-wifi' : 'mdi-wifi-off' }}
            </v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ socketStore.isConnected ? t('home.stats.connected') : t('home.stats.disconnected') }}</div>
            <div class="stat-label">{{ t('home.stats.realtimeChannel') }}</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Membership Banner (Pro) -->
    <v-card
      v-if="userStore.isProMember"
      variant="flat"
      rounded="lg"
      class="pro-banner mb-6"
    >
      <v-card-text class="d-flex align-center flex-wrap ga-3 pa-4">
        <div class="pro-badge">
          <v-icon size="20" color="amber">mdi-crown</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="font-weight-bold text-body-1">{{ t('home.proMember') }}</div>
          <div class="text-body-2 text-medium-emphasis" v-if="memberExpiry">
            {{ t('home.validUntil', { date: memberExpiry }) }}
          </div>
        </div>
        <v-btn variant="tonal" size="small" rounded="lg" @click="router.push('/member')">
          {{ t('home.manageSubscription') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Upgrade Banner (Free) -->
    <v-card
      v-else
      variant="flat"
      rounded="lg"
      class="upgrade-banner mb-6"
    >
      <v-card-text class="d-flex align-center flex-wrap ga-3 pa-4">
        <div class="upgrade-icon">
          <v-icon size="20" color="primary">mdi-rocket-launch</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="font-weight-bold text-body-1">{{ t('home.upgradeToPro') }}</div>
          <div class="text-body-2 text-medium-emphasis">
            {{ t('home.upgradeDesc') }}
          </div>
        </div>
        <v-btn variant="tonal" color="primary" size="small" rounded="lg" @click="router.push('/member')">
          {{ t('home.learnMore') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- My Servers Section -->
    <div class="section-header mb-3">
      <div class="d-flex align-center">
        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-server-network</v-icon>
        <span class="section-title">{{ t('home.myServers') }}</span>
        <v-spacer />
        <v-btn
          variant="text"
          size="small"
          color="primary"
          @click="router.push('/server')"
        >
          {{ t('home.viewAll') }}
          <v-icon end size="16">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="mb-8">
      <!-- Loading skeleton -->
      <v-row v-if="serversLoading" dense>
        <v-col v-for="i in 3" :key="i" cols="12" sm="6" md="4">
          <v-skeleton-loader type="card" rounded="lg" />
        </v-col>
      </v-row>

      <!-- Server cards -->
      <v-row v-else-if="servers.length > 0" dense>
        <v-col
          v-for="server in servers.slice(0, 6)"
          :key="server.serverId"
          cols="12"
          sm="6"
          md="4"
          class="d-flex"
        >
          <v-card
            variant="flat"
            rounded="lg"
            class="server-mini-card flex-grow-1"
            :to="server.uuid ? `/server/${server.uuid}` : undefined"
          >
            <v-card-text class="pa-3 d-flex align-center ga-3">
              <div
                class="server-dot"
                :class="isServerOnline(server) ? 'server-dot--online' : 'server-dot--offline'"
              />
              <div class="flex-grow-1 min-width-0">
                <div class="text-body-2 font-weight-bold text-truncate">{{ server.serverName || t('home.unnamedServer') }}</div>
                <div class="server-mini-meta text-truncate">
                  {{ server.serverIp || (server.uuid ? server.uuid.substring(0, 12) + '...' : t('home.notConfigured')) }}
                </div>
              </div>
              <div class="d-flex align-center ga-1">
                <v-chip v-if="server.active" size="x-small" color="amber" variant="tonal">Pro</v-chip>
                <v-icon size="14" class="text-disabled">mdi-chevron-right</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty state -->
      <v-card v-else variant="flat" rounded="lg" class="empty-servers">
        <v-card-text class="text-center pa-8">
          <v-icon size="40" class="text-disabled mb-3">mdi-server-plus</v-icon>
          <div class="text-body-1 font-weight-medium mb-1">{{ t('home.noServers') }}</div>
          <div class="text-body-2 text-disabled mb-4">{{ t('home.addFirstServer') }}</div>
          <v-btn variant="tonal" color="primary" rounded="lg" @click="router.push('/server')">
            <v-icon start>mdi-plus</v-icon>
            {{ t('home.addServer') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </div>

    <!-- Quick Links Section -->
    <div class="section-header mb-3">
      <div class="d-flex align-center">
        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-lightning-bolt</v-icon>
        <span class="section-title">{{ t('home.quickLinks') }}</span>
      </div>
    </div>

    <v-row class="mb-8" dense>
      <v-col
        v-for="link in quickLinks"
        :key="link.label"
        cols="6"
        sm="3"
        class="d-flex"
      >
        <v-card
          variant="flat"
          rounded="lg"
          class="quick-link-card flex-grow-1"
          :href="link.href"
          target="_blank"
        >
          <v-card-text class="pa-4">
            <div class="quick-link-icon mb-3" :style="{ background: link.color + '15' }">
              <v-icon size="20" :color="link.color">{{ link.icon }}</v-icon>
            </div>
            <div class="text-body-2 font-weight-bold mb-1">{{ link.label }}</div>
            <div class="quick-link-desc">{{ link.desc }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modules Section -->
    <div class="section-header mb-3">
      <div class="d-flex align-center">
        <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-puzzle</v-icon>
        <span class="section-title">{{ t('home.ecosystem') }}</span>
        <v-spacer />
        <v-btn
          variant="text"
          size="small"
          color="primary"
          @click="router.push('/module')"
        >
          {{ t('home.viewAll') }}
          <v-icon end size="16">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="modules-container">
      <!-- Loading -->
      <div v-if="modulesLoading" class="modules-list">
        <v-skeleton-loader v-for="i in modulesPerPage" :key="i" type="list-item-avatar-two-line" class="module-skeleton" />
      </div>

      <!-- Module list -->
      <div v-else-if="modules.length > 0" class="modules-list">
        <div
          v-for="mod in modules"
          :key="mod.id"
          class="module-row"
          @click="router.push(`/module/${mod.identifyString}`)"
        >
          <v-avatar size="38" rounded="lg" class="module-icon" color="transparent">
            <v-img v-if="mod.icon" :src="mod.icon" />
            <v-icon v-else size="20" color="primary">mdi-puzzle</v-icon>
          </v-avatar>
          <div class="module-info">
            <div class="module-name">{{ mod.name }}</div>
            <div class="module-desc">{{ mod.shortDescription || mod.identifyString }}</div>
          </div>
          <v-icon size="14" class="text-disabled ml-auto flex-shrink-0">mdi-chevron-right</v-icon>
        </div>

        <!-- Pagination -->
        <div v-if="moduleTotalPages > 1" class="module-pagination">
          <v-btn
            icon
            size="x-small"
            variant="text"
            :disabled="modulePage <= 1"
            @click="fetchModules(modulePage - 1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </v-btn>
          <span class="pagination-text">{{ modulePage }} / {{ moduleTotalPages }}</span>
          <v-btn
            icon
            size="x-small"
            variant="text"
            :disabled="modulePage >= moduleTotalPages"
            @click="fetchModules(modulePage + 1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="modules-empty">
        <v-icon size="28" class="text-disabled mb-2">mdi-puzzle-outline</v-icon>
        <div class="text-body-2 text-disabled">{{ t('home.noModules') }}</div>
      </div>
    </div>
  </v-container>
</template>

<style scoped>
.home-page {
  max-width: 1000px;
  margin: 0 auto;
  padding-top: 16px;
  padding-bottom: 40px;
}

/* Hero */
.hero-section {
  padding: 8px 0 16px;
}

.hero-greeting {
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.hero-subtitle {
  font-size: 0.85rem;
  margin-top: 4px;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

/* Stats */
.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.stat-card:hover {
  border-color: rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.45);
  font-weight: 500;
  margin-top: 2px;
}

/* Pro / Upgrade Banners */
.pro-banner {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.04) 100%);
  border: 1px solid rgba(245, 158, 11, 0.15);
}

.pro-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.upgrade-banner {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.upgrade-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Section Headers */
.section-header {
  padding: 0 2px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

/* Server Mini Cards */
.server-mini-card {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  transition: all 0.15s ease;
}

.server-mini-card:hover {
  border-color: rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.server-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.server-dot--online {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
}

.server-dot--offline {
  background: rgba(var(--v-theme-on-surface), 0.2);
}

.server-mini-meta {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.4);
  font-family: monospace;
}

.min-width-0 {
  min-width: 0;
}

/* Empty servers */
.empty-servers {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

/* Quick Links */
.quick-link-card {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  transition: all 0.15s ease;
}

.quick-link-card:hover {
  border-color: rgba(var(--v-theme-on-surface), 0.12);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
}

.quick-link-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-link-desc {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.4);
  line-height: 1.4;
}

/* Modules */
.modules-container {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 16px;
  overflow: hidden;
}

.modules-list {
  display: flex;
  flex-direction: column;
}

.module-skeleton {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.04);
}

.module-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.12s ease;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.04);
}

.module-row:last-of-type {
  border-bottom: none;
}

.module-row:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}

.module-icon {
  background: rgba(var(--v-theme-primary), 0.08);
  flex-shrink: 0;
}

.module-info {
  min-width: 0;
  flex: 1;
}

.module-name {
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.module-desc {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.4);
  line-height: 1.4;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.module-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.pagination-text {
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.45);
  font-weight: 500;
  min-width: 40px;
  text-align: center;
}

.modules-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}
</style>

<route lang="yaml">
meta:
  requiresAuth: true
</route>
